import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Phone } from "lucide-react";
import { toast } from "sonner";
import { PageTitle } from "../components/site/PageTitle";
import { ArtworkUploader } from "../components/site/ArtworkUploader";
import { CustomerFields, emptyCustomer } from "../components/site/CustomerFields";
import { useSite } from "../context/SiteContext";
import { api, errMsg, telLink } from "../lib/api";
import { QUOTE_SERVICES } from "../lib/constants";

export default function Quote() {
  const { settings: s = {} } = useSite();
  const nav = useNavigate();
  const [cust, setCust] = useState(emptyCustomer);
  const [f, setF] = useState({ service: "", quantity: "", message: "", website: "" });
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { needed_by, ...customer } = cust;
      const { data } = await api.post("/quotes", { customer, needed_by, ...f, files });
      nav(`/thank-you?n=${data.number}&t=quote`);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="pb-20">
      <PageTitle badge="ASAP & Co." title="Request a Quote" subtitle="Send the details for a custom apparel, print, transfer, decal, embroidery, or branding project." testId="quote-title" />
      <div className="mx-auto grid max-w-6xl gap-8 px-6 lg:grid-cols-[1fr_320px]">
        <form onSubmit={submit} className="panel space-y-5 p-6 sm:p-8" data-testid="quote-form">
          <CustomerFields value={cust} onChange={setCust} prefix="quote" />
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="label">What are you looking for? *</label>
              <select className="field" required value={f.service} onChange={(e) => setF({ ...f, service: e.target.value })} data-testid="quote-service-select">
                <option value="">Select a service</option>
                {(s.quote_services?.length ? s.quote_services : QUOTE_SERVICES).map((o) => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div>
              <label className="label">Estimated Quantity</label>
              <input className="field" value={f.quantity} onChange={(e) => setF({ ...f, quantity: e.target.value })} placeholder="e.g. 24 shirts" data-testid="quote-quantity-input" />
            </div>
          </div>
          <div>
            <label className="label">Project Details *</label>
            <textarea className="field min-h-[140px]" required value={f.message} onChange={(e) => setF({ ...f, message: e.target.value })} placeholder="Colors, sizes, print placement, garment style..." data-testid="quote-message-input" />
          </div>
          <input className="hidden" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => setF({ ...f, website: e.target.value })} aria-hidden="true" />
          <div>
            <label className="label">Upload Logo / Design (optional)</label>
            <ArtworkUploader files={files} onChange={setFiles} testId="quote-artwork" />
          </div>
          <button className="btn-red w-full sm:w-auto" disabled={busy} data-testid="quote-submit-button">{busy ? "Sending..." : "Send Request"}</button>
        </form>
        <aside className="space-y-4">
          <div className="panel p-6">
            <h3 className="red-title text-xl">Contact Us</h3>
            {s.phone && <a href={telLink(s.phone)} className="mt-4 flex items-center gap-3 font-display text-xl font-bold hover:text-[#60A5FA]" data-testid="quote-phone-link"><Phone className="h-5 w-5 text-[#E5202A]" />{s.phone}</a>}
            {s.email && <a href={`mailto:${s.email}`} className="mt-3 flex items-center gap-3 break-all text-sm hover:text-[#60A5FA]" data-testid="quote-email-link"><Mail className="h-5 w-5 shrink-0 text-[#E5202A]" />{s.email}</a>}
            {s.location && <p className="mt-3 text-sm text-slate-400">{s.location}</p>}
          </div>
          <div className="panel p-6 text-sm text-slate-400">Already know what you need? Use the custom order form to add items to your cart and check out.</div>
        </aside>
      </div>
    </div>
  );
}
